<template>
	<v-container fluid class="financeiro-page pa-8 pt-12">
		<div class="financeiro-header">
			<div>
				<h1 class="financeiro-title">Financeiro</h1>
				<p class="financeiro-subtitle">
					Controle contas a pagar e a receber em uma unica tela, com baixa rapida e saldo previsto.
				</p>
			</div>
			<div class="financeiro-header__actions">
				<v-btn class="financeiro-secondary-btn" outlined @click="contasDialogOpen = true">
					<v-icon left size="18">mdi-bank-outline</v-icon>
					Contas
				</v-btn>
				<v-btn class="financeiro-secondary-btn" outlined @click="multiplosDialogOpen = true">
					<v-icon left size="18">mdi-file-excel-outline</v-icon>
					Multiplos
				</v-btn>
				<v-btn class="financeiro-new-btn" depressed @click="abrirCadastro()">
					<v-icon left size="18">mdi-plus</v-icon>
					Novo lancamento
				</v-btn>
			</div>
		</div>

		<v-row class="mt-6" dense>
			<v-col
				v-for="card in resumoCards"
				:key="card.label"
				cols="12"
				sm="6"
				md="3"
			>
				<v-card class="financeiro-stat" outlined>
					<div class="financeiro-stat__content">
						<div>
							<div class="financeiro-stat__label">{{ card.label }}</div>
							<div class="financeiro-stat__value" :style="{ color: card.color || '#1f2a44' }">
								{{ formatCurrency(card.value) }}
							</div>
						</div>
						<div class="financeiro-stat__icon" :style="{ background: card.background }">
							<v-icon :color="card.color" size="20">{{ card.icon }}</v-icon>
						</div>
					</div>
				</v-card>
			</v-col>
		</v-row>

		<v-card class="financeiro-table-card mt-6" outlined>
			<div class="financeiro-table-card__toolbar">
				<v-menu offset-y left :close-on-content-click="false">
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							icon
							class="financeiro-icon-btn"
							v-bind="attrs"
							v-on="on"
							title="Colunas"
							aria-label="Colunas"
						>
							<v-icon size="18">mdi-cog-outline</v-icon>
						</v-btn>
					</template>

					<v-card class="financeiro-colunas-card" outlined>
						<v-card-title class="financeiro-colunas-card__title">
							<span>Colunas visiveis</span>
							<v-spacer />
							<v-btn text small color="#10b981" @click="restaurarColunasPadrao">
								Padrao
							</v-btn>
						</v-card-title>
						<v-divider />
						<v-card-text class="pt-4 pb-3">
							<div class="financeiro-colunas-card__hint">
								Escolha quais colunas do financeiro devem aparecer na tabela.
							</div>
							<v-checkbox
								v-for="column in columnOptions"
								:key="column.value"
								:input-value="isColumnVisible(column.value)"
								:label="column.text"
								hide-details
								dense
								class="financeiro-colunas-card__checkbox mt-0"
								@change="toggleVisibleColumn(column.value)"
							/>
						</v-card-text>
					</v-card>
				</v-menu>

				<v-badge
					:value="hasActiveFilters"
					color="red"
					dot
					overlap
				>
					<v-btn
						icon
						class="financeiro-icon-btn"
						title="Filtros"
						aria-label="Filtros"
						@click="abrirFiltros"
					>
						<v-icon size="18">mdi-filter-outline</v-icon>
					</v-btn>
				</v-badge>
			</div>

			<v-data-table
				class="financeiro-table"
				:headers="visibleHeaders"
				:items="lancamentosFiltrados"
				:loading="loading"
				:item-class="getRowClass"
				loading-text="Carregando lancamentos..."
				no-data-text="Nenhum lancamento cadastrado"
				no-results-text="Nenhum resultado encontrado"
				:items-per-page="10"
				:footer-props="{
					itemsPerPageText: 'Itens por Pagina',
					itemsPerPageOptions: [10, 15, 20],
					pageText: '{0}-{1} de {2}'
				}"
			>
				<template v-slot:[`item.descricao`]='{ item }'>
					<span class="financeiro-table__nowrap" :title="item.descricao || '-'">
						{{ item.descricao || '-' }}
					</span>
				</template>

				<template v-slot:[`item.categoria`]='{ item }'>
					<span class="financeiro-table__nowrap" :title="item.categoria || '-'">
						{{ item.categoria || '-' }}
					</span>
				</template>

				<template v-slot:[`item.observacoes`]='{ item }'>
					<span class="financeiro-table__nowrap" :title="item.observacoes || '-'">
						{{ item.observacoes || '-' }}
					</span>
				</template>

				<template v-slot:[`item.tipo`]='{ item }'>
					<span class="financeiro-chip" :class="tipoChipClass(item.tipo)">
						<v-icon size="14" class="mr-1">{{ item.tipo === 'Receber' ? 'mdi-arrow-down-bold-circle-outline' : 'mdi-arrow-up-bold-circle-outline' }}</v-icon>
						{{ item.tipo }}
					</span>
				</template>

				<template v-slot:[`item.status`]='{ item }'>
					<span class="financeiro-chip" :class="statusChipClass(item.status)">
						{{ item.status }}
					</span>
				</template>

				<template v-slot:[`item.data_prevista`]='{ item }'>
					<span>{{ formatDate(item.data_prevista) }}</span>
				</template>

				<template v-slot:[`item.data_baixa`]='{ item }'>
					<span>{{ formatDate(item.data_baixa) }}</span>
				</template>

				<template v-slot:[`item.conta_bancaria_nome`]='{ item }'>
					<span>{{ item.conta_bancaria_nome || '-' }}</span>
				</template>

				<template v-slot:[`item.veiculos_label`]='{ item }'>
					<span>{{ formatVeiculo(item) }}</span>
				</template>

				<template v-slot:[`item.valor`]='{ item }'>
					<strong :style="{ color: item.tipo === 'Receber' ? '#0f9f6e' : '#dc2626' }">
						{{ formatCurrency(item.valor) }}
					</strong>
				</template>

				<template v-slot:[`item.acoes`]='{ item }'>
					<v-menu offset-y left>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small class="financeiro-actions" v-bind="attrs" v-on="on">
								<v-icon color="#12b57a">mdi-dots-vertical</v-icon>
							</v-btn>
						</template>

						<v-list dense class="financeiro-actions__menu">
							<v-list-item @click="abrirDetalhes(item)">
								<v-list-item-icon>
									<v-icon color="#2563eb" size="18">mdi-eye-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Detalhes</v-list-item-title>
							</v-list-item>

							<v-divider />

							<v-list-item @click="abrirCadastro(item)">
								<v-list-item-icon>
									<v-icon color="#12b57a" size="18">mdi-pencil-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Editar</v-list-item-title>
							</v-list-item>

							<v-divider v-if="item.status !== 'Baixado'" />

							<v-list-item v-if="item.status !== 'Baixado'" @click="abrirBaixa(item)">
								<v-list-item-icon>
									<v-icon color="#2563eb" size="18">mdi-check-circle-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Dar baixa</v-list-item-title>
							</v-list-item>

							<v-divider />

							<v-list-item @click="abrirExclusao(item)">
								<v-list-item-icon>
									<v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Excluir</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
			</v-data-table>
		</v-card>

		<v-dialog v-model="filterDialogOpen" max-width="420" persistent>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Filtrar lancamentos</span>
					<v-spacer />
					<v-btn icon @click="filterDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-0">
					<v-row dense>
						<v-col cols="12">
							<v-text-field
								v-model="filterForm.search"
								label="Buscar descricao ou categoria"
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								clearable
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								v-model="filterForm.tipo"
								:items="tiposFiltro"
								label="Tipo"
								outlined
								dense
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								v-model="filterForm.status"
								:items="statusFiltro"
								label="Status"
								outlined
								dense
							/>
						</v-col>
						<v-col cols="12">
							<v-autocomplete
								v-model="filterForm.conta"
								:items="contasFiltroOptions"
								item-text="nome"
								item-value="nome"
								label="Conta bancaria"
								outlined
								dense
								clearable
								no-data-text="Nenhuma conta encontrada"
							/>
						</v-col>
						<v-col cols="12">
							<v-autocomplete
								v-model="filterForm.placas"
								:items="veiculosFiltroOptions"
								item-text="label"
								item-value="value"
								label="Placas"
								outlined
								dense
								multiple
								small-chips
								deletable-chips
								clearable
								no-data-text="Nenhuma placa encontrada"
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								v-model="filterForm.periodo_inicio"
								label="Periodo inicial"
								type="date"
								outlined
								dense
								clearable
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								v-model="filterForm.periodo_fim"
								label="Periodo final"
								type="date"
								outlined
								dense
								clearable
							/>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="px-6 pb-6">
					<v-btn text @click="limparFiltros">Limpar</v-btn>
					<v-spacer />
					<v-btn text @click="filterDialogOpen = false">Cancelar</v-btn>
					<v-btn class="financeiro-new-btn" depressed @click="aplicarFiltros">
						Filtrar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<CadastroFinanceiroDialog
			v-model="cadastroDialogOpen"
			:lancamento="lancamentoEmEdicao"
			:lancamentos="lancamentos"
			:categorias="categoriasDisponiveis"
			:contas-bancarias="contasBancarias"
			:veiculos="veiculos"
			@saved="handleLancamentoSalvo"
		/>

		<ContasFinanceiroDialog
			v-model="contasDialogOpen"
			:contas="contasBancarias"
			@saved="handleContaSalva"
		/>

		<MultiplosFinanceiroDialog
			v-model="multiplosDialogOpen"
			:categorias="categoriasDisponiveis"
			:contas-bancarias="contasBancarias"
			:veiculos="veiculos"
			@saved="handleMultiplosSalvos"
		/>

		<v-dialog v-model="detalhesDialogOpen" max-width="780" scrollable>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Detalhes do lancamento</span>
					<v-spacer />
					<v-btn icon @click="detalhesDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-4 pb-2">
					<div v-if="lancamentoDetalhes" class="financeiro-details">
						<div class="financeiro-details__header">
							<div>
								<div class="financeiro-details__title">
									{{ lancamentoDetalhes.descricao || '-' }}
								</div>
								<div class="financeiro-details__subtitle">
									{{ lancamentoDetalhes.categoria || 'Sem categoria' }}
								</div>
							</div>
							<div class="financeiro-details__chips">
								<span class="financeiro-chip" :class="tipoChipClass(lancamentoDetalhes.tipo)">
									<span class="financeiro-chip__label">Tipo:</span>
									{{ lancamentoDetalhes.tipo || '-' }}
								</span>
								<span class="financeiro-chip" :class="statusChipClass(lancamentoDetalhes.status)">
									<span class="financeiro-chip__label">Status:</span>
									{{ lancamentoDetalhes.status || '-' }}
								</span>
							</div>
						</div>

						<div class="financeiro-details-grid">
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Valor</span>
								<strong class="financeiro-detail-value">{{ formatCurrency(lancamentoDetalhes.valor) }}</strong>
							</div>
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Data prevista</span>
								<strong class="financeiro-detail-value">{{ formatDate(lancamentoDetalhes.data_prevista) }}</strong>
							</div>
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Data baixa</span>
								<strong class="financeiro-detail-value">{{ formatDate(lancamentoDetalhes.data_baixa) }}</strong>
							</div>
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Conta bancaria</span>
								<strong class="financeiro-detail-value">{{ lancamentoDetalhes.conta_bancaria_nome || '-' }}</strong>
							</div>
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Veiculos</span>
								<strong class="financeiro-detail-value">{{ formatVeiculo(lancamentoDetalhes) }}</strong>
							</div>
							<div class="financeiro-detail-item">
								<span class="financeiro-detail-label">Observacao</span>
								<strong class="financeiro-detail-value">{{ lancamentoDetalhes.observacoes || '-' }}</strong>
							</div>
						</div>

						<div v-if="detalhesInstallmentInfo" class="financeiro-installments-summary">
							<div>
								<span>Total parcelado</span>
								<strong>{{ formatCurrency(totalParcelasDetalhes) }}</strong>
							</div>
							<div>
								<span>Parcelas</span>
								<strong>{{ detalhesInstallmentInfo.totalInstallments }}</strong>
							</div>
							<div>
								<span>Parcela atual</span>
								<strong>{{ detalhesInstallmentInfo.installmentNumber }}/{{ detalhesInstallmentInfo.totalInstallments }}</strong>
							</div>
						</div>

						<div v-if="detalhesInstallmentInfo" class="financeiro-parcelas">
							<div class="financeiro-parcelas__title">Outras parcelas</div>
							<div
								v-for="parcela in parcelasDetalhes"
								:key="parcela.id"
								class="financeiro-parcela-item"
								:class="{ 'financeiro-parcela-item--current': parcela.id === lancamentoDetalhes.id }"
							>
								<div class="financeiro-parcela-item__main">
									<strong>Parcela {{ parcela.installmentNumber }}/{{ parcela.totalInstallments }}</strong>
									<span>{{ parcela.descricao || '-' }}</span>
								</div>
								<div class="financeiro-parcela-item__meta">
									<span class="financeiro-chip" :class="statusChipClass(parcela.status)">
										{{ parcela.status || '-' }}
									</span>
									<strong>{{ formatCurrency(parcela.valor) }}</strong>
									<span>{{ formatDate(parcela.data_prevista) }}</span>
									<span v-if="parcela.id === lancamentoDetalhes.id" class="financeiro-parcela-item__current">
										Atual
									</span>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-3">
					<v-spacer />
					<v-btn class="financeiro-new-btn" depressed @click="detalhesDialogOpen = false">
						Fechar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="baixaDialogOpen" max-width="420" persistent>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Dar baixa</span>
					<v-spacer />
					<v-btn icon :disabled="savingBaixa" @click="baixaDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="financeiro-confirmacao__texto mb-4">
						Confirme a data de {{ baixaDialogItem.tipo === 'Receber' ? 'recebimento' : 'pagamento' }} de
						<strong>{{ baixaDialogItem.descricao || '-' }}</strong>.
					</div>
					<v-text-field
						v-model="baixaForm.data_baixa"
						:label="baixaDialogItem.tipo === 'Receber' ? 'Data do recebimento' : 'Data do pagamento'"
						type="date"
						outlined
						dense
					/>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="savingBaixa" @click="baixaDialogOpen = false">Cancelar</v-btn>
					<v-btn class="financeiro-new-btn" depressed :loading="savingBaixa" :disabled="savingBaixa" @click="confirmarBaixa">
						Salvar baixa
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Excluir lancamento</span>
					<v-spacer />
					<v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="financeiro-confirmacao__texto">
						Tem certeza que deseja excluir <strong>{{ lancamentoExclusao.descricao || '-' }}</strong>?
					</div>
					<div class="financeiro-confirmacao__aviso">
						Esta acao nao pode ser desfeita.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="financeiro-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import CadastroFinanceiroDialog from './cadastroFinanceiro.vue';
import ContasFinanceiroDialog from './contasFinanceiroDialog.vue';
import MultiplosFinanceiroDialog from './multiplosFinanceiroDialog.vue';
import financeiroServices from './financeiroServices';
import veiculoServices from '../veiculos/veiculoServices';
import { formatCurrencyDisplay } from './financeiroCurrency';

const FINANCEIRO_VISIBLE_COLUMNS_STORAGE_KEY = 'machado_web_transportadoras.financeiro.visible_columns';
const AVAILABLE_HEADERS = [
	{ text: 'Previsto', value: 'data_prevista', width: '120px' },
	{ text: 'Baixa', value: 'data_baixa', width: '120px' },
	{ text: 'Descricao', sortable: false, value: 'descricao' },
	{ text: 'Categoria', sortable: false, value: 'categoria' },
	{ text: 'Observacao', sortable: false, value: 'observacoes', width: '220px' },
	{ text: 'Conta', sortable: false, value: 'conta_bancaria_nome', width: '180px' },
	{ text: 'Veiculos', sortable: false, value: 'veiculos_label', width: '260px' },
	{ text: 'Tipo', sortable: false, value: 'tipo', width: '130px' },
	{ text: 'Status', value: 'status', width: '120px' },
	{ text: 'Valor', sortable: false, value: 'valor', width: '140px', align: 'end' },
	{ text: 'Acoes', value: 'acoes', sortable: false, width: '90px', align: 'center' },
];
const DEFAULT_VISIBLE_COLUMNS = AVAILABLE_HEADERS
	.filter((header) => header.value !== 'acoes' && header.value !== 'observacoes')
	.map((header) => header.value);

const CATEGORIAS_PADRAO = [
	'Frete',
	'Combustivel',
	'Manutencao',
	'Pedagio',
	'Pneus Novos',
	'Recapagem de pneus',
	'Salario',
	'Imposto',
	'Seguro',
	'Fornecedor',
	'Cliente',
	'Outros',
];

function getLancamentoSortTime(item) {
	const referenceDate = item?.data_baixa || item?.data_prevista;

	if (!referenceDate) {
		return Number.MIN_SAFE_INTEGER;
	}

	const timestamp = new Date(`${String(referenceDate).slice(0, 10)}T00:00:00`).getTime();
	return Number.isNaN(timestamp) ? Number.MIN_SAFE_INTEGER : timestamp;
}

function formatDateInput(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

function getPeriodoMesAtual() {
	const today = new Date();
	const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
	const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

	return {
		periodo_inicio: formatDateInput(firstDay),
		periodo_fim: formatDateInput(lastDay),
	};
}

function parseInstallmentDescription(descricao) {
	const normalized = String(descricao || '').trim();
	const match = normalized.match(/^(.*)\((\d+)\/(\d+)\)\s*$/);

	if (!match) {
		return null;
	}

	const installmentNumber = Number(match[2]);
	const totalInstallments = Number(match[3]);

	if (!installmentNumber || !totalInstallments || installmentNumber > totalInstallments) {
		return null;
	}

	return {
		baseDescription: match[1].trim(),
		installmentNumber,
		totalInstallments,
	};
}

export default {
	name: 'ListaFinanceiro',

	components: {
		CadastroFinanceiroDialog,
		ContasFinanceiroDialog,
		MultiplosFinanceiroDialog,
	},

	data() {
		const periodoMesAtual = getPeriodoMesAtual();

		return {
			loading: false,
			deleting: false,
			savingBaixa: false,
			cadastroDialogOpen: false,
			contasDialogOpen: false,
			multiplosDialogOpen: false,
			deleteDialogOpen: false,
			baixaDialogOpen: false,
			detalhesDialogOpen: false,
			lancamentos: [],
			contasBancarias: [],
			veiculos: [],
			lancamentoEmEdicao: null,
			lancamentoExclusao: {},
			lancamentoDetalhes: null,
			baixaDialogItem: {},
			baixaForm: {
				data_baixa: new Date().toISOString().slice(0, 10),
			},
			filterDialogOpen: false,
			filterForm: {
				search: '',
				tipo: 'Todos',
				status: 'Todos',
				conta: null,
				placas: [],
				periodo_inicio: periodoMesAtual.periodo_inicio,
				periodo_fim: periodoMesAtual.periodo_fim,
			},
			activeFilters: {
				search: '',
				tipo: 'Todos',
				status: 'Todos',
				conta: null,
				placas: [],
				periodo_inicio: periodoMesAtual.periodo_inicio,
				periodo_fim: periodoMesAtual.periodo_fim,
			},
			tiposFiltro: ['Todos', 'Receber', 'Pagar'],
			statusFiltro: ['Todos', 'Pendente', 'Baixado'],
			visibleColumnValues: [...DEFAULT_VISIBLE_COLUMNS],
		};
	},

	computed: {
		columnOptions() {
			return AVAILABLE_HEADERS.filter((header) => header.value !== 'acoes');
		},

		visibleHeaders() {
			return AVAILABLE_HEADERS.filter(
				(header) => header.value === 'acoes' || this.visibleColumnValues.includes(header.value),
			);
		},

		categoriasDisponiveis() {
			const categoriasLancadas = this.lancamentos
				.map((item) => item.categoria)
				.filter(Boolean);

			return [...new Set([...CATEGORIAS_PADRAO, ...categoriasLancadas])];
		},

		contasFiltroOptions() {
			return this.contasBancarias
				.filter((c) => c.nome)
				.sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
		},

		veiculosOptions() {
			return this.veiculos.map((item) => ({
				label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
				value: item.id,
			}));
		},

		veiculosFiltroOptions() {
			return this.veiculos
				.filter((item) => item.placa)
				.map((item) => ({
					label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
					value: item.id,
				}))
				.sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'));
		},

		lancamentosFiltrados() {
			const search = (this.activeFilters.search || '').toLowerCase();

			return this.lancamentos.filter((item) => {
				const matchesSearch = !search || [item.descricao, item.categoria, item.observacoes, item.conta_bancaria_nome, item.veiculos_label, item.veiculo_placa, item.veiculo_modelo]
					.filter(Boolean)
					.some((field) => String(field).toLowerCase().includes(search));

				const matchesTipo = this.activeFilters.tipo === 'Todos' || item.tipo === this.activeFilters.tipo;
				const matchesStatus = this.activeFilters.status === 'Todos' || item.status === this.activeFilters.status;
				const matchesConta = !this.activeFilters.conta || item.conta_bancaria_nome === this.activeFilters.conta;
				const selectedPlacas = Array.isArray(this.activeFilters.placas) ? this.activeFilters.placas : [];
				const itemVeiculoIds = Array.isArray(item.veiculo_ids) ? item.veiculo_ids : [];
				const matchesPlacas = !selectedPlacas.length || selectedPlacas.some((placaId) => itemVeiculoIds.includes(placaId));
				const periodoInicio = this.activeFilters.periodo_inicio || '';
				const periodoFim = this.activeFilters.periodo_fim || '';
				const dataPrevista = String(item.data_prevista || '').slice(0, 10);
				const matchesPeriodoInicio = !periodoInicio || (dataPrevista && dataPrevista >= periodoInicio);
				const matchesPeriodoFim = !periodoFim || (dataPrevista && dataPrevista <= periodoFim);

				return matchesSearch && matchesTipo && matchesStatus && matchesConta && matchesPlacas && matchesPeriodoInicio && matchesPeriodoFim;
			}).sort((firstItem, secondItem) => {
				const sortDifference = getLancamentoSortTime(secondItem) - getLancamentoSortTime(firstItem);

				if (sortDifference !== 0) {
					return sortDifference;
				}

				return String(firstItem?.descricao || '').localeCompare(String(secondItem?.descricao || ''), 'pt-BR');
			});
		},

		hasActiveFilters() {
			const f = this.activeFilters;
			return Boolean(
				f.search ||
				(f.tipo && f.tipo !== 'Todos') ||
				(f.status && f.status !== 'Todos') ||
				f.conta ||
				(Array.isArray(f.placas) && f.placas.length) ||
				f.periodo_inicio ||
				f.periodo_fim
			);
		},

		totais() {
			return this.lancamentosFiltrados.reduce((acc, item) => {
				const valor = Number(item.valor || 0);

				if (item.status === 'Baixado') {
					acc.saldoAtual += item.tipo === 'Receber' ? valor : -valor;
				}

				if (item.status !== 'Baixado' && item.tipo === 'Receber') {
					acc.aReceber += valor;
				}

				if (item.status !== 'Baixado' && item.tipo === 'Pagar') {
					acc.aPagar += valor;
				}

				return acc;
			}, {
				saldoAtual: 0,
				aReceber: 0,
				aPagar: 0,
			});
		},

		resumoCards() {
			const saldoPrevisto = this.totais.saldoAtual + this.totais.aReceber - this.totais.aPagar;

			return [
				{
					label: 'Saldo atual',
					value: this.totais.saldoAtual,
					icon: 'mdi-wallet-outline',
					color: this.totais.saldoAtual >= 0 ? '#0f9f6e' : '#dc2626',
					background: this.totais.saldoAtual >= 0 ? '#ecfdf5' : '#fef2f2',
				},
				{
					label: 'A receber',
					value: this.totais.aReceber,
					icon: 'mdi-arrow-down-bold-circle-outline',
					color: '#0f9f6e',
					background: '#ecfdf5',
				},
				{
					label: 'A pagar',
					value: this.totais.aPagar,
					icon: 'mdi-arrow-up-bold-circle-outline',
					color: '#dc2626',
					background: '#fef2f2',
				},
				{
					label: 'Saldo previsto',
					value: saldoPrevisto,
					icon: 'mdi-finance',
					color: saldoPrevisto >= 0 ? '#1f7a5a' : '#b91c1c',
					background: '#f8fafc',
				},
			];
		},

		detalhesInstallmentInfo() {
			return parseInstallmentDescription(this.lancamentoDetalhes?.descricao);
		},

		parcelasDetalhes() {
			if (!this.lancamentoDetalhes || !this.detalhesInstallmentInfo) {
				return [];
			}

			return this.lancamentos
				.map((item) => ({
					...item,
					...(parseInstallmentDescription(item.descricao) || {}),
				}))
				.filter((item) => this.isSameInstallmentGroup(item, this.lancamentoDetalhes, this.detalhesInstallmentInfo))
				.sort((firstItem, secondItem) => firstItem.installmentNumber - secondItem.installmentNumber);
		},

		totalParcelasDetalhes() {
			return this.parcelasDetalhes.reduce((total, item) => total + Number(item.valor || 0), 0);
		},
	},

	created() {
		this.loadVisibleColumnsPreference();
		this.carregarLancamentos();
		this.carregarContas();
		this.carregarVeiculos();
	},

	methods: {
		normalizeVisibleColumns(values) {
			const allowedValues = AVAILABLE_HEADERS
				.filter((header) => header.value !== 'acoes')
				.map((header) => header.value);
			const selectedValues = new Set(Array.isArray(values) ? values : []);
			const normalizedValues = allowedValues.filter((value) => selectedValues.has(value));

			return normalizedValues.length ? normalizedValues : [...DEFAULT_VISIBLE_COLUMNS];
		},

		loadVisibleColumnsPreference() {
			try {
				const storedColumns = localStorage.getItem(FINANCEIRO_VISIBLE_COLUMNS_STORAGE_KEY);
				if (!storedColumns) {
					return;
				}

				this.visibleColumnValues = this.normalizeVisibleColumns(JSON.parse(storedColumns));
			} catch (error) {
				this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
			}
		},

		persistVisibleColumnsPreference() {
			try {
				localStorage.setItem(
					FINANCEIRO_VISIBLE_COLUMNS_STORAGE_KEY,
					JSON.stringify(this.visibleColumnValues),
				);
			} catch (error) {
				// Ignora falhas de armazenamento local sem interromper a tela.
			}
		},

		isColumnVisible(columnValue) {
			return this.visibleColumnValues.includes(columnValue);
		},

		toggleVisibleColumn(columnValue) {
			const isVisible = this.isColumnVisible(columnValue);

			if (isVisible && this.visibleColumnValues.length === 1) {
				this.$toasted?.show('Mantenha ao menos uma coluna visivel na tabela.');
				return;
			}

			const nextValues = isVisible
				? this.visibleColumnValues.filter((value) => value !== columnValue)
				: [...this.visibleColumnValues, columnValue];

			this.visibleColumnValues = this.normalizeVisibleColumns(nextValues);
			this.persistVisibleColumnsPreference();
		},

		restaurarColunasPadrao() {
			this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
			this.persistVisibleColumnsPreference();
			this.$toasted?.show('Colunas restauradas para o padrao.');
		},

		abrirFiltros() {
			this.filterForm = {
				...this.activeFilters,
				placas: Array.isArray(this.activeFilters.placas) ? [...this.activeFilters.placas] : [],
			};
			this.filterDialogOpen = true;
		},

		aplicarFiltros() {
			this.activeFilters = {
				...this.filterForm,
				placas: Array.isArray(this.filterForm.placas) ? [...this.filterForm.placas] : [],
			};
			this.filterDialogOpen = false;
		},

		limparFiltros() {
			const empty = {
				search: '',
				tipo: 'Todos',
				status: 'Todos',
				conta: null,
				placas: [],
				periodo_inicio: '',
				periodo_fim: '',
			};
			this.filterForm = { ...empty };
			this.activeFilters = { ...empty };
			this.filterDialogOpen = false;
		},

		async carregarContas() {
			try {
				const response = await financeiroServices.listarContas();
				this.contasBancarias = Array.isArray(response.data) ? response.data : [];
			} catch (error) {
				this.contasBancarias = [];
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel carregar as contas bancarias';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			}
		},

		async carregarVeiculos() {
			try {
				const response = await veiculoServices.listar();
				this.veiculos = Array.isArray(response?.data) ? response.data : [];
			} catch (error) {
				this.veiculos = [];
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel carregar os veiculos';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			}
		},

		async carregarLancamentos() {
			this.loading = true;

			try {
				const response = await financeiroServices.listar();
				this.lancamentos = Array.isArray(response.data) ? response.data : [];
			} catch (error) {
				this.lancamentos = [];
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel carregar o financeiro';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.loading = false;
			}
		},

		formatCurrency(value) {
			return formatCurrencyDisplay(value);
		},

		formatVeiculo(item) {
			if (item?.veiculos_label) {
				return item.veiculos_label;
			}

			if (!item?.veiculo_placa) {
				return '-';
			}

			return item.veiculo_modelo
				? `${item.veiculo_placa} - ${item.veiculo_modelo}`
				: item.veiculo_placa;
		},

		formatDate(value) {
			if (!value) {
				return '-';
			}

			const [year, month, day] = String(value).slice(0, 10).split('-');
			return year && month && day ? `${day}/${month}/${year}` : '-';
		},

		getRowClass(item) {
			return item.tipo === 'Receber' ? 'financeiro-row--receber' : 'financeiro-row--pagar';
		},

		tipoChipClass(tipo) {
			return tipo === 'Receber' ? 'financeiro-chip--receber' : 'financeiro-chip--pagar';
		},

		statusChipClass(status) {
			return status === 'Baixado' ? 'financeiro-chip--baixado' : 'financeiro-chip--pendente';
		},

		normalizeIdList(values) {
			return (Array.isArray(values) ? values : [])
				.map((value) => Number(value))
				.filter((value) => Number.isFinite(value))
				.sort((a, b) => a - b)
				.join(',');
		},

		isSameInstallmentGroup(item, currentItem, currentInfo) {
			const itemInfo = parseInstallmentDescription(item?.descricao);

			if (!itemInfo) {
				return false;
			}

			const itemCreatedAt = String(item?.created_at || '');
			const currentCreatedAt = String(currentItem?.created_at || '');
			const matchesCreatedAt = !itemCreatedAt || !currentCreatedAt || itemCreatedAt === currentCreatedAt;

			return itemInfo.baseDescription === currentInfo.baseDescription &&
				itemInfo.totalInstallments === currentInfo.totalInstallments &&
				item.tipo === currentItem.tipo &&
				item.categoria === currentItem.categoria &&
				Number(item.valor || 0) === Number(currentItem.valor || 0) &&
				Number(item.conta_bancaria_id || 0) === Number(currentItem.conta_bancaria_id || 0) &&
				this.normalizeIdList(item.veiculo_ids) === this.normalizeIdList(currentItem.veiculo_ids) &&
				matchesCreatedAt;
		},

		abrirDetalhes(item) {
			this.lancamentoDetalhes = item ? { ...item } : null;
			this.detalhesDialogOpen = true;
		},

		abrirCadastro(item = null) {
			this.lancamentoEmEdicao = item ? { ...item } : null;
			this.cadastroDialogOpen = true;
		},

		handleLancamentoSalvo() {
			this.cadastroDialogOpen = false;
			this.lancamentoEmEdicao = null;
			this.carregarLancamentos();
		},

		handleContaSalva() {
			this.carregarContas();
			this.carregarLancamentos();
		},

		handleMultiplosSalvos() {
			this.multiplosDialogOpen = false;
			this.carregarLancamentos();
		},

		abrirExclusao(item) {
			this.lancamentoExclusao = item;
			this.deleteDialogOpen = true;
		},

		async confirmarExclusao() {
			if (!this.lancamentoExclusao?.id) {
				return;
			}

			this.deleting = true;

			try {
				await financeiroServices.excluir(this.lancamentoExclusao.id);
				this.deleteDialogOpen = false;
				this.lancamentoExclusao = {};
				this.carregarLancamentos();
				if (this.$toasted) {
					this.$toasted.show('Lancamento excluido com sucesso');
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel excluir o lancamento';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.deleting = false;
			}
		},

		abrirBaixa(item) {
			this.baixaDialogItem = item;
			this.baixaForm = {
				data_baixa: new Date().toISOString().slice(0, 10),
			};
			this.baixaDialogOpen = true;
		},

		async confirmarBaixa() {
			if (!this.baixaDialogItem?.id) {
				return;
			}

			this.savingBaixa = true;

			try {
				await financeiroServices.darBaixa(this.baixaDialogItem.id, this.baixaForm);
				this.baixaDialogOpen = false;
				this.baixaDialogItem = {};
				this.carregarLancamentos();
				if (this.$toasted) {
					this.$toasted.show('Baixa registrada com sucesso');
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel registrar a baixa';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.savingBaixa = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.financeiro-page {
	background: linear-gradient(180deg, #f8fbff 0%, #f3f7fb 100%);
	min-height: 100%;
	font-family: "Inter", sans-serif;
}

.financeiro-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
	flex-wrap: wrap;
}

.financeiro-header__actions {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.financeiro-title {
	color: #10b981;
	font-size: 22px;
	font-weight: 800;
	line-height: 1.2;
	margin: 0;
}

.financeiro-subtitle {
	color: #7a8ca6;
	font-size: 13px;
	margin: 6px 0 0;
}

.financeiro-new-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 14px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 16px 32px rgba(18, 181, 122, 0.22) !important;
}

.financeiro-secondary-btn {
	border-color: #10b981 !important;
	color: #10b981 !important;
	font-weight: 700;
	border-radius: 14px;
	text-transform: none;
	padding: 0 18px !important;
	background: #ffffff !important;
}

.financeiro-delete-btn {
	background: linear-gradient(135deg, #f87171 0%, #ef4444 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
}

.financeiro-stat {
	border-radius: 16px !important;
	border: 1px solid #e6ebf1 !important;
	background: rgba(255, 255, 255, 0.9) !important;
	box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
	height: 100%;
}

.financeiro-stat__content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 18px;
	gap: 12px;
	height: 100%;
}

.financeiro-stat__label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7a90;
	margin-bottom: 6px;
}

.financeiro-stat__value {
	font-size: 18px;
	font-weight: 800;
	line-height: 1.2;
	color: #1f2a44;
}

.financeiro-stat__icon {
	width: 38px;
	height: 38px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.financeiro-table-card,
.financeiro-dialog {
	border-radius: 24px !important;
	border: 1px solid #e6ebf1 !important;
	background: rgba(255, 255, 255, 0.96) !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.08) !important;
}

.financeiro-table-card__toolbar {
	display: flex;
	justify-content: flex-end;
	padding: 0 12px;
	margin-top: -20px;
}

.financeiro-icon-btn {
	width: 38px;
	height: 38px;
	min-width: 38px !important;
	border-radius: 12px !important;
	background-color: #ffffff !important;
	color: #10b981 !important;
	border: 2px solid #10b981 !important;
}

.financeiro-colunas-card {
	width: 320px;
	max-width: calc(100vw - 32px);
	border-radius: 18px !important;
	border: 1px solid #dce8e4 !important;
	box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12) !important;
}

.financeiro-colunas-card__title {
	padding: 16px 18px;
	font-size: 15px;
	font-weight: 800;
	color: #10233b;
}

.financeiro-colunas-card__hint {
	font-size: 12px;
	line-height: 1.5;
	color: #7a8ca6;
	margin-bottom: 10px;
}

.financeiro-colunas-card__checkbox {
	margin-bottom: 4px;
}

.financeiro-colunas-card__checkbox::v-deep .v-label {
	font-size: 13px;
	color: #1f2a44;
}

.financeiro-table::v-deep thead th {
	color: #7a8ca6 !important;
	font-weight: 800 !important;
	font-size: 11px !important;
	letter-spacing: 0.08em;
	background: #ffffff;
	border-bottom: 1px solid #e6ebf1 !important;
}

.financeiro-table::v-deep tbody td {
	border-bottom: 1px solid rgba(230, 235, 241, 0.9) !important;
	font-size: 13px;
	color: #1f2a44;
	padding-top: 14px !important;
	padding-bottom: 14px !important;
}

.financeiro-table__nowrap {
	display: block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.financeiro-table::v-deep tbody tr.financeiro-row--receber {
	background: #f0fdf4 !important;
}

.financeiro-table::v-deep tbody tr.financeiro-row--pagar {
	background: #fef2f2 !important;
}

.financeiro-chip {
	display: inline-flex;
	align-items: center;
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 700;
}

.financeiro-chip__label {
	margin-right: 4px;
	font-weight: 800;
}

.financeiro-chip--receber {
	background: rgba(16, 185, 129, 0.12);
	color: #0f9f6e;
}

.financeiro-chip--pagar {
	background: rgba(239, 68, 68, 0.12);
	color: #dc2626;
}

.financeiro-chip--baixado {
	background: rgba(37, 99, 235, 0.12);
	color: #2563eb;
}

.financeiro-chip--pendente {
	background: rgba(217, 119, 6, 0.14);
	color: #b45309;
}

.financeiro-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.financeiro-confirmacao__texto {
	font-size: 15px;
	line-height: 1.6;
	color: #334155;
}

.financeiro-confirmacao__aviso {
	margin-top: 10px;
	font-size: 13px;
	color: #7b8aa2;
}

.financeiro-details {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.financeiro-details__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding-bottom: 10px;
	border-bottom: 1px solid #e6ebf1;
}

.financeiro-details__title {
	font-size: 17px;
	font-weight: 800;
	line-height: 1.35;
	color: #10233b;
	word-break: break-word;
}

.financeiro-details__subtitle {
	margin-top: 4px;
	font-size: 13px;
	font-weight: 600;
	color: #7a8ca6;
}

.financeiro-details__chips {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	flex-wrap: wrap;
}

.financeiro-details-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 24px;
	row-gap: 0;
}

.financeiro-detail-item {
	display: grid;
	grid-template-columns: 116px minmax(0, 1fr);
	align-items: baseline;
	gap: 10px;
	padding: 6px 0;
	border-bottom: 1px solid #eef2f7;
	min-width: 0;
}

.financeiro-detail-label {
	display: block;
	font-size: 11px;
	font-weight: 800;
	line-height: 1.3;
	color: #7a8ca6;
	text-transform: uppercase;
}

.financeiro-detail-value {
	display: block;
	font-size: 13px;
	line-height: 1.45;
	color: #1f2a44;
	word-break: break-word;
}

.financeiro-installments-summary {
	display: flex;
	align-items: center;
	gap: 24px;
	flex-wrap: wrap;
	padding: 10px 0;
	border-top: 1px solid #e6ebf1;
	border-bottom: 1px solid #e6ebf1;
}

.financeiro-installments-summary span {
	display: block;
	margin-bottom: 2px;
	font-size: 11px;
	font-weight: 800;
	color: #7a8ca6;
	text-transform: uppercase;
}

.financeiro-installments-summary strong {
	font-size: 14px;
	color: #10233b;
}

.financeiro-parcelas {
	display: flex;
	flex-direction: column;
	gap: 0;
}

.financeiro-parcelas__title {
	margin-bottom: 4px;
	font-size: 13px;
	font-weight: 800;
	color: #10233b;
}

.financeiro-parcela-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;
	padding: 8px 0;
	border-bottom: 1px solid #eef2f7;
}

.financeiro-parcela-item--current {
	padding-left: 10px;
	border-left: 3px solid #10b981;
	background: #f0fdf4;
}

.financeiro-parcela-item__main {
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
}

.financeiro-parcela-item__main strong {
	font-size: 13px;
	color: #10233b;
}

.financeiro-parcela-item__main span {
	font-size: 12px;
	color: #6b7a90;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.financeiro-parcela-item__meta {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	flex-wrap: wrap;
	font-size: 12px;
	color: #6b7a90;
}

.financeiro-parcela-item__meta strong {
	color: #10233b;
}

.financeiro-parcela-item__current {
	padding: 4px 8px;
	border-radius: 999px;
	background: #dcfce7;
	color: #15803d !important;
	font-size: 11px !important;
	font-weight: 800;
}

@media (max-width: 960px) {
	.financeiro-page {
		padding: 24px 16px !important;
	}

	.financeiro-title {
		font-size: 28px;
	}

	.financeiro-header__actions {
		width: 100%;
	}

	.financeiro-details__header,
	.financeiro-parcela-item {
		flex-direction: column;
		align-items: stretch;
	}

	.financeiro-details-grid,
	.financeiro-installments-summary {
		grid-template-columns: 1fr;
	}

	.financeiro-detail-item {
		grid-template-columns: 100px minmax(0, 1fr);
	}

	.financeiro-installments-summary {
		align-items: flex-start;
		gap: 8px;
	}

	.financeiro-details__chips,
	.financeiro-parcela-item__meta {
		justify-content: flex-start;
	}

}
</style>
